import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { EmojiPicker, EmojiPickerContent, EmojiPickerSearch, EmojiPickerFooter } from '../ui/emoji-picker';
import {
  MessageSquare, X, Send, Paperclip, Smile, Search, Hash,
  Users, Plus, Settings, Phone, Video, MoreVertical, Check,
  CheckCheck, Image, File, Download, Trash2, Reply, Copy,
  Pin, Flag, AtSign, Clock, ChevronDown, Loader, Bot, 
  PanelLeftClose, PanelLeft, PanelRight, Folder, Bell, User,
  FileText, Globe, Heart, CheckSquare, HelpCircle, Lightbulb, Zap,
  Volume2, Palette, Lock, Info, Circle
} from 'lucide-react';
import socketService from '../../services/socketService';
import { getChannels, getChannelMessages, getChannelMembers, createChannel } from '../../services/chatApi';
import AIAssistPanel from './AIAssistPanel';
import '../../styles/premium-chat.css';
import toast from '../../utils/toast';
import { AI_FEATURES_CONFIG, TIER_COLORS, hasFeatureAccess, getFeatureConfig } from '../../config/aiFeatures';
import { canUseFeature, getRemainingUses, incrementUsage } from '../../utils/aiUsageTracker';
import { useUser } from '../../contexts/UserContext';
import { useAIDrawer } from '../../contexts/AIDrawerContext';

const TeamChatModal = ({ isOpen, onClose, currentUser }) => {
  const { userTier } = useUser();
  const { isAIDrawerOpen } = useAIDrawer();
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [channelMembers, setChannelMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [messageReactions, setMessageReactions] = useState({}); // { messageId: { emoji: count } }
  const [replyTo, setReplyTo] = useState(null);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionPosition, setMentionPosition] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [expandedThreads, setExpandedThreads] = useState(new Set());
  const [threadReplies, setThreadReplies] = useState({});
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelDesc, setNewChannelDesc] = useState('');
  const [showAIAssist, setShowAIAssist] = useState(false);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [callType, setCallType] = useState(null); // 'video' or 'audio'
  const [isInCall, setIsInCall] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [callParticipants, setCallParticipants] = useState([]);
  const [editingMessage, setEditingMessage] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [messageReadBy, setMessageReadBy] = useState({}); // { messageId: [userId1, userId2] }
  const [isDragging, setIsDragging] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const [leftMenuActive, setLeftMenuActive] = useState('channels'); // 'channels', 'dms', 'pinned', 'search', 'ai', 'calls', 'settings'
  const [rightPanelTab, setRightPanelTab] = useState('members'); // 'members', 'pinned', 'files', 'activity'
  const [showRightPanel, setShowRightPanel] = useState(true);
  const [showActivityDropdown, setShowActivityDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [userStatus, setUserStatus] = useState('online'); // 'online', 'away', 'busy', 'offline'
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const modalRef = useRef(null);

  // Auto-close team chat when AI drawer opens
  useEffect(() => {
    if (isAIDrawerOpen && isOpen) {
      onClose();
    }
  }, [isAIDrawerOpen, isOpen, onClose]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      // Add delay to prevent immediate close on open
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Initialize Socket.io connection
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && isOpen) {
      socketService.connect(token);
      loadChannels();
    }

    return () => {
      if (activeChannel) {
        socketService.leaveChannel(activeChannel.id);
      }
    };
  }, [isOpen]);

  // Socket event listeners
  useEffect(() => {
    if (!isOpen) return;

    // Listen for new messages
    socketService.on('message:new', (message) => {
      try {
        setMessages(prev => [...prev, message]);
        scrollToBottom();
      } catch (error) {
        console.error('Error handling new message:', error);
      }
    });

    // Listen for sent confirmation
    socketService.on('message:sent', ({ tempId, message }) => {
      try {
        setMessages(prev => prev.map(msg => 
          msg.tempId === tempId ? { ...message, status: 'sent' } : msg
        ));
      } catch (error) {
        console.error('Error handling message sent:', error);
      }
    });

    // Listen for channel history
    socketService.on('channel:history', ({ channelId, history }) => {
      try {
        if (activeChannel?.id === channelId) {
          setMessages(history);
          scrollToBottom();
        }
      } catch (error) {
        console.error('Error handling channel history:', error);
      }
    });

    // Typing indicator
    socketService.on('typing:user', ({ userId, userName, isTyping: typing }) => {
      try {
        if (typing && userId !== currentUser.id) {
          setTypingUsers(prev => [...new Set([...prev, userName])]);
        } else {
          setTypingUsers(prev => prev.filter(name => name !== userName));
        }
      } catch (error) {
        console.error('Error handling typing indicator:', error);
      }
    });

    // User status updates
    socketService.on('user:status', ({ userId, status }) => {
      try {
        if (status === 'online') {
          setOnlineUsers(prev => new Set([...prev, userId]));
        } else {
          setOnlineUsers(prev => {
            const newSet = new Set(prev);
            newSet.delete(userId);
            return newSet;
          });
        }
      } catch (error) {
        console.error('Error handling user status:', error);
      }
    });

    return () => {
      socketService.removeAllListeners('message:new');
      socketService.removeAllListeners('message:sent');
      socketService.removeAllListeners('channel:history');
      socketService.removeAllListeners('typing:user');
      socketService.removeAllListeners('user:status');
    };
  }, [isOpen, activeChannel, currentUser]);

  // Load channels
  const loadChannels = async () => {
    try {
      const response = await getChannels();
      setChannels(response.data || []);
      
      // Auto-select first channel
      if (response.data && response.data.length > 0 && !activeChannel) {
        selectChannel(response.data[0]);
      }
    } catch (error) {
      console.error('Error loading channels:', error);
    }
  };

  // Select channel
  const selectChannel = async (channel) => {
    if (activeChannel) {
      socketService.leaveChannel(activeChannel.id);
    }

    setActiveChannel(channel);
    setMessages([]);
    socketService.joinChannel(channel.id);

    // Load channel members
    try {
      const response = await getChannelMembers(channel.id);
      setChannelMembers(response.data || []);
    } catch (error) {
      console.error('Error loading members:', error);
    }
  };

  // Send message
  const sendMessage = () => {
    // If editing, save edit instead
    if (editingMessage) {
      saveEditMessage();
      return;
    }

    console.log('sendMessage called', { messageInput, activeChannel });
    
    if (!messageInput.trim() && !selectedFile) {
      console.log('No message content');
      return;
    }
    
    if (!activeChannel) {
      console.error('No active channel selected');
      toast.warning('Please select a channel first!');
      return;
    }

    const tempId = Date.now();
    const newMessage = {
      tempId,
      channelId: activeChannel.id,
      content: messageInput,
      type: selectedFile ? 'file' : 'text',
      metadata: selectedFile ? { fileName: selectedFile.name, fileSize: selectedFile.size } : {},
      user: currentUser,
      createdAt: new Date(),
      status: 'sending',
      parentId: replyTo?.id || null,
      replyTo: replyTo ? {
        id: replyTo.id,
        content: replyTo.content,
        user: replyTo.user
      } : null
    };

    console.log('Adding message to UI:', newMessage);
    
    // Add to UI immediately
    setMessages(prev => {
      const updated = [...prev, newMessage];
      console.log('Updated messages:', updated);
      return updated;
    });
    
    // Send via Socket.io
    try {
      socketService.sendMessage({
        channelId: activeChannel.id,
        content: messageInput,
        type: newMessage.type,
        metadata: newMessage.metadata,
        tempId
      });
    } catch (error) {
      console.error('Error sending message:', error);
      // Update message status to error
      setMessages(prev => prev.map(msg => 
        msg.tempId === tempId ? { ...msg, status: 'error' } : msg
      ));
    }

    // Fallback: Mark as sent after 2 seconds if no response
    setTimeout(() => {
      setMessages(prev => prev.map(msg => 
        msg.tempId === tempId && msg.status === 'sending' 
          ? { ...msg, status: 'sent' } 
          : msg
      ));
    }, 2000);

    // Clear input
    setMessageInput('');
    setSelectedFile(null);
    setReplyTo(null);
    
    setTimeout(() => scrollToBottom(), 100);
  };

  // Handle typing
  const handleTyping = (e) => {
    const value = e.target.value;
    setMessageInput(value);

    // Check for @ mentions
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtSymbol !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtSymbol + 1);
      // Only show mentions if @ is at start or after a space, and no space after @
      const charBeforeAt = lastAtSymbol > 0 ? textBeforeCursor[lastAtSymbol - 1] : ' ';
      if ((charBeforeAt === ' ' || lastAtSymbol === 0) && !textAfterAt.includes(' ')) {
        setShowMentions(true);
        setMentionSearch(textAfterAt.toLowerCase());
        setMentionPosition(lastAtSymbol);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }

    if (!isTyping && activeChannel) {
      setIsTyping(true);
      socketService.startTyping(activeChannel.id);
    }

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Stop typing after 2 seconds
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      if (activeChannel) {
        socketService.stopTyping(activeChannel.id);
      }
    }, 2000);
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };

  // Handle drag and drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      setSelectedFile(file);
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };

  // Get file icon based on type
  const getFileIcon = (fileName) => {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    const iconMap = {
      pdf: '📄',
      doc: '📝',
      docx: '📝',
      xls: '📊',
      xlsx: '📊',
      ppt: '📽️',
      pptx: '📽️',
      zip: '🗜️',
      rar: '🗜️',
      mp4: '🎥',
      mp3: '🎵',
      jpg: '🖼️',
      jpeg: '🖼️',
      png: '🖼️',
      gif: '🖼️',
      svg: '🖼️',
    };
    return iconMap[ext] || '📎';
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Handle emoji selection
  const handleEmojiClick = (emoji) => {
    // Extract the actual emoji character from the emoji object
    const emojiChar = emoji?.native || emoji?.emoji || emoji;
    setMessageInput(prev => prev + emojiChar);
    setShowEmojiPicker(false);
  };

  // Handle AI feature click with tier checking
  const handleAIFeature = (featureName, featureAction) => {
    // Check if channel is selected
    if (!activeChannel) {
      toast.warning('Please select a channel first!');
      return;
    }

    // Get user's tier (default to 'free' if not set)
    const tier = userTier || 'free';
    
    // Check if user has access to this feature
    if (!hasFeatureAccess(tier, featureName)) {
      const requiredTier = featureName === 'suggestions' ? 'enterprise' : 'premium';
      const tierName = requiredTier.charAt(0).toUpperCase() + requiredTier.slice(1);
      toast.warning(`Upgrade to ${tierName} to unlock this feature!`);
      // TODO: Show upgrade modal
      return;
    }

    // Get feature config
    const featureConfig = getFeatureConfig(tier, featureName);
    if (!featureConfig) {
      toast.error('Feature configuration error');
      return;
    }

    // Check usage limits
    const limit = featureConfig.limit;
    if (!canUseFeature(featureName, limit)) {
      toast.warning(`Daily limit reached! Upgrade for more uses.`);
      // TODO: Show upgrade modal
      return;
    }

    // Get remaining uses
    const remaining = getRemainingUses(featureName, limit);
    
    // Execute the feature
    featureAction();
    
    // Increment usage
    incrementUsage(featureName);
    
    // Show remaining uses (if not unlimited)
    if (limit !== -1) {
      const newRemaining = remaining - 1;
      if (newRemaining <= 3 && newRemaining > 0) {
        toast.info(`${newRemaining} uses remaining today`);
      }
    }
  };

  // Add reaction to message
  const addReaction = (messageId, emoji) => {
    setMessageReactions(prev => ({
      ...prev,
      [messageId]: {
        ...prev[messageId],
        [emoji]: (prev[messageId]?.[emoji] || 0) + 1
      }
    }));
    
    // Emit to socket for real-time updates
    socketService.emit('message:react', {
      messageId,
      emoji,
      userId: currentUser.id
    });
  };

  // Toggle thread expansion
  const toggleThread = (messageId) => {
    setExpandedThreads(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
        // Load thread replies if not already loaded
        if (!threadReplies[messageId]) {
          loadThreadReplies(messageId);
        }
      }
      return newSet;
    });
  };

  // Load thread replies
  const loadThreadReplies = async (messageId) => {
    try {
      // TODO: API call to get thread replies
      // For now, use mock data
      setThreadReplies(prev => ({
        ...prev,
        [messageId]: []
      }));
    } catch (error) {
      console.error('Error loading thread replies:', error);
    }
  };

  // Reply to message
  const replyToMessage = (message) => {
    setReplyTo(message);
    // Focus on input
    document.querySelector('textarea[placeholder*="Message"]')?.focus();
  };

  // Pin message
  const pinMessage = (message) => {
    setPinnedMessages(prev => {
      if (prev.find(m => m.id === message.id)) {
        return prev.filter(m => m.id !== message.id);
      }
      return [...prev, message];
    });
    
    socketService.emit('message:pin', {
      messageId: message.id,
      channelId: activeChannel.id
    });
  };

  // Search messages
  const searchMessages = (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const results = messages.filter(msg =>
      msg.content?.toLowerCase().includes(query.toLowerCase()) ||
      msg.user?.name?.toLowerCase().includes(query.toLowerCase())
    );
    setSearchResults(results);
  };

  // Start video/audio call
  const startCall = async (type = 'video') => {
    try {
      setCallType(type);
      setShowVideoCall(true);
      
      // Get user media
      const stream = await navigator.mediaDevices.getUserMedia({
        video: type === 'video',
        audio: true
      });
      
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      setIsInCall(true);
      setCallParticipants([currentUser]);
      
      // Emit call start to socket
      socketService.emit('call:start', {
        channelId: activeChannel.id,
        type,
        userId: currentUser.id
      });
    } catch (error) {
      console.error('Error starting call:', error);
      toast.error('Failed to access camera/microphone. Please check permissions.');
    }
  };

  // End call
  const endCall = () => {
    // Stop all tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    
    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }
    
    setShowVideoCall(false);
    setIsInCall(false);
    setCallParticipants([]);
    setIsMuted(false);
    setIsCameraOff(false);
    setIsScreenSharing(false);
    
    // Emit call end to socket
    socketService.emit('call:end', {
      channelId: activeChannel.id,
      userId: currentUser.id
    });
  };

  // Toggle mute
  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  // Toggle camera
  const toggleCamera = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsCameraOff(!videoTrack.enabled);
      }
    }
  };

  // Start screen sharing
  const startScreenShare = async () => {
    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: true
      });
      
      // Replace video track with screen track
      const screenTrack = screenStream.getVideoTracks()[0];
      const sender = peerConnectionRef.current?.getSenders().find(s => s.track?.kind === 'video');
      
      if (sender) {
        sender.replaceTrack(screenTrack);
      }
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = screenStream;
      }
      
      setIsScreenSharing(true);
      
      // Stop screen sharing when user stops it
      screenTrack.onended = () => {
        stopScreenShare();
      };
    } catch (error) {
      console.error('Error sharing screen:', error);
    }
  };

  // Stop screen sharing
  const stopScreenShare = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      const sender = peerConnectionRef.current?.getSenders().find(s => s.track?.kind === 'video');
      
      if (sender && videoTrack) {
        sender.replaceTrack(videoTrack);
      }
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = localStreamRef.current;
      }
    }
    
    setIsScreenSharing(false);
  };

  // Edit message
  const startEditMessage = (message) => {
    if (message.user?.id !== currentUser.id) return;
    setEditingMessage(message);
    setMessageInput(message.content);
  };

  const saveEditMessage = () => {
    if (!editingMessage || !messageInput.trim()) return;

    const updatedMessage = {
      ...editingMessage,
      content: messageInput,
      edited: true,
      editedAt: new Date()
    };

    setMessages(prev => prev.map(msg => 
      msg.id === editingMessage.id ? updatedMessage : msg
    ));

    // Emit to socket
    socketService.emit('message:edit', {
      messageId: editingMessage.id,
      content: messageInput,
      channelId: activeChannel.id
    });

    setEditingMessage(null);
    setMessageInput('');
  };

  const cancelEdit = () => {
    setEditingMessage(null);
    setMessageInput('');
  };

  // Delete message
  const deleteMessage = (messageId) => {
    setShowDeleteConfirm(messageId);
  };

  const confirmDelete = () => {
    if (!showDeleteConfirm) return;

    setMessages(prev => prev.map(msg =>
      msg.id === showDeleteConfirm
        ? { ...msg, deleted: true, content: 'Message deleted' }
        : msg
    ));

    // Emit to socket
    socketService.emit('message:delete', {
      messageId: showDeleteConfirm,
      channelId: activeChannel.id
    });

    setShowDeleteConfirm(null);
  };

  // Mark message as read
  const markAsRead = (messageId) => {
    if (!messageReadBy[messageId]?.includes(currentUser.id)) {
      setMessageReadBy(prev => ({
        ...prev,
        [messageId]: [...(prev[messageId] || []), currentUser.id]
      }));

      socketService.emit('message:read', {
        messageId,
        userId: currentUser.id,
        channelId: activeChannel.id
      });
    }
  };

  // Get read status
  const getReadStatus = (message) => {
    const readers = messageReadBy[message.id] || [];
    const otherReaders = readers.filter(id => id !== message.user?.id);
    
    if (otherReaders.length === 0) return 'sent';
    if (otherReaders.length === channelMembers.length - 1) return 'read-all';
    return 'read-some';
  };

  // Insert mention
  const insertMention = (member) => {
    const beforeMention = messageInput.substring(0, mentionPosition);
    const afterMention = messageInput.substring(mentionPosition + mentionSearch.length + 1);
    const newMessage = `${beforeMention}@${member.name} ${afterMention}`;
    setMessageInput(newMessage);
    setShowMentions(false);
    setMentionSearch('');
  };

  // Filter members for mention
  const filteredMembersForMention = channelMembers.filter(member =>
    member.name?.toLowerCase().includes(mentionSearch)
  );

  // Render message with highlighted mentions
  const renderMessageWithMentions = (content) => {
    if (!content || typeof content !== 'string') return content;
    
    const mentionRegex = /@(\w+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let hasMatches = false;

    while ((match = mentionRegex.exec(content)) !== null) {
      hasMatches = true;
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`}>
            {content.substring(lastIndex, match.index)}
          </span>
        );
      }
      
      // Add highlighted mention
      const mentionName = match[1];
      const isSelf = mentionName.toLowerCase() === currentUser.name?.toLowerCase();
      parts.push(
        <span
          key={`mention-${match.index}`}
          className={`${
            isSelf 
              ? 'bg-cyan-600/30 text-cyan-300 border border-cyan-600/50' 
              : 'bg-blue-600/20 text-blue-300'
          } px-1.5 py-0.5 rounded font-medium`}
        >
          @{mentionName}
        </span>
      );
      
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (hasMatches && lastIndex < content.length) {
      parts.push(
        <span key={`text-${lastIndex}`}>
          {content.substring(lastIndex)}
        </span>
      );
    }

    return hasMatches ? <>{parts}</> : content;
  };

  // Create new channel
  const handleCreateChannel = async () => {
    console.log('handleCreateChannel called', { newChannelName, newChannelDesc });
    
    if (!newChannelName.trim()) {
      console.log('Channel name is empty');
      toast.warning('Please enter a channel name');
      return;
    }

    try {
      console.log('Creating channel...');
      const response = await createChannel({
        name: newChannelName,
        description: newChannelDesc,
        type: 'public'
      });

      console.log('Channel created:', response.data);
      setChannels(prev => [...prev, response.data]);
      setShowCreateChannel(false);
      setNewChannelName('');
      setNewChannelDesc('');
      selectChannel(response.data);
    } catch (error) {
      console.error('Error creating channel:', error);
      toast.error('Failed to create channel: ' + (error.response?.data?.message || error.message));
    }
  };

  // Scroll to bottom
  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Format timestamp
  const formatTime = (date) => {
    const d = new Date(date);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // Filter messages by search
  const filteredMessages = messages.filter(msg =>
    msg.content?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  const modalContent = (
    <div 
      ref={modalRef}
      className="premium-chat-container flex premium-slide-in" 
      style={{ 
        position: 'fixed',
        zIndex: 9999, 
        bottom: '20px',
        right: '20px',
        width: '980px',
        maxWidth: '88vw',
        height: '600px',
        maxHeight: '80vh',
        pointerEvents: 'auto'
      }}
    >
      <div className="premium-chat-inner flex w-full">
      {/* LEFT PANEL - Icon Navigation */}
      <div className="w-16 bg-gray-900 border-r border-gray-700 flex flex-col items-center py-4 gap-1">
        {/* Channels */}
        <button
          onClick={() => setLeftMenuActive('channels')}
          className={`group relative p-3 rounded-lg transition-all ${
            leftMenuActive === 'channels'
              ? 'bg-cyan-600 text-white'
              : 'text-gray-400 hover:bg-gray-800 hover:text-white'
          }`}
          title="Channels"
        >
          <Hash className="w-5 h-5" />
          <div className="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
            Channels
          </div>
        </button>

        {/* Direct Messages */}
        <button
          onClick={() => setLeftMenuActive('dms')}
          className={`group relative p-3 rounded-lg transition-all ${
            leftMenuActive === 'dms'
              ? 'bg-cyan-600 text-white'
              : 'text-gray-400 hover:bg-gray-800 hover:text-white'
          }`}
          title="Direct Messages"
        >
          <User className="w-5 h-5" />
          <div className="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
            Direct Messages
          </div>
        </button>

        {/* Pinned */}
        <button
          onClick={() => setLeftMenuActive('pinned')}
          className={`group relative p-3 rounded-lg transition-all ${
            leftMenuActive === 'pinned'
              ? 'bg-cyan-600 text-white'
              : 'text-gray-400 hover:bg-gray-800 hover:text-white'
          }`}
          title="Pinned"
        >
          <Pin className="w-5 h-5" />
          <div className="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
            Pinned Messages
          </div>
        </button>

        {/* Advanced Search */}
        <button
          onClick={() => setLeftMenuActive('search')}
          className={`group relative p-3 rounded-lg transition-all ${
            leftMenuActive === 'search'
              ? 'bg-cyan-600 text-white'
              : 'text-gray-400 hover:bg-gray-800 hover:text-white'
          }`}
          title="Advanced Search"
        >
          <Search className="w-5 h-5" />
          <div className="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
            Advanced Search
          </div>
        </button>

        {/* AI Assistant */}
        <button
          onClick={() => setLeftMenuActive('ai')}
          className={`group relative p-3 rounded-lg transition-all ${
            leftMenuActive === 'ai'
              ? 'bg-cyan-600 text-white'
              : 'text-gray-400 hover:bg-gray-800 hover:text-white'
          }`}
          title="AI Assistant"
        >
          <Bot className="w-5 h-5" />
          <div className="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
            AI Assistant
          </div>
        </button>

        {/* Settings */}
        <button
          onClick={() => setLeftMenuActive('settings')}
          className={`group relative p-3 rounded-lg transition-all ${
            leftMenuActive === 'settings'
              ? 'bg-cyan-600 text-white'
              : 'text-gray-400 hover:bg-gray-800 hover:text-white'
          }`}
          title="Settings"
        >
          <Settings className="w-5 h-5" />
          <div className="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
            Settings
          </div>
        </button>

        {/* Spacer */}
        <div className="flex-1" />

        {/* User Profile with Status */}
        <div className="relative">
          <button
            onClick={() => setShowProfileDropdown(!showProfileDropdown)}
            className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center text-white text-sm font-semibold cursor-pointer hover:ring-2 hover:ring-cyan-400 transition-all relative"
          >
            {currentUser.name?.charAt(0) || '?'}
            {/* Status Indicator */}
            <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-gray-900 ${
              userStatus === 'online' ? 'bg-green-500' :
              userStatus === 'away' ? 'bg-yellow-500' :
              userStatus === 'busy' ? 'bg-red-500' :
              'bg-gray-500'
            }`} />
          </button>

          {/* Profile Dropdown */}
          {showProfileDropdown && (
            <div className="absolute bottom-full left-0 mb-2 w-56 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
              {/* User Info */}
              <div className="p-3 border-b border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center text-white text-sm font-semibold">
                    {currentUser.name?.charAt(0) || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm font-medium truncate">{currentUser.name || 'User'}</div>
                    <div className="text-gray-400 text-xs truncate">{currentUser.email || 'user@plugly.ai'}</div>
                  </div>
                </div>
              </div>

              {/* Status Options */}
              <div className="p-2">
                <div className="text-gray-400 text-xs font-medium px-2 py-1">Set Status</div>
                <button
                  onClick={() => {
                    setUserStatus('online');
                    setShowProfileDropdown(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded transition-colors ${
                    userStatus === 'online' ? 'bg-gray-700' : 'hover:bg-gray-700/50'
                  }`}
                >
                  <Circle className="w-3 h-3 fill-green-500 text-green-500" />
                  <span className="text-white text-sm">Online</span>
                  {userStatus === 'online' && <Check className="w-4 h-4 text-cyan-400 ml-auto" />}
                </button>
                <button
                  onClick={() => {
                    setUserStatus('away');
                    setShowProfileDropdown(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded transition-colors ${
                    userStatus === 'away' ? 'bg-gray-700' : 'hover:bg-gray-700/50'
                  }`}
                >
                  <Circle className="w-3 h-3 fill-yellow-500 text-yellow-500" />
                  <span className="text-white text-sm">Away</span>
                  {userStatus === 'away' && <Check className="w-4 h-4 text-cyan-400 ml-auto" />}
                </button>
                <button
                  onClick={() => {
                    setUserStatus('busy');
                    setShowProfileDropdown(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded transition-colors ${
                    userStatus === 'busy' ? 'bg-gray-700' : 'hover:bg-gray-700/50'
                  }`}
                >
                  <Circle className="w-3 h-3 fill-red-500 text-red-500" />
                  <span className="text-white text-sm">Busy</span>
                  {userStatus === 'busy' && <Check className="w-4 h-4 text-cyan-400 ml-auto" />}
                </button>
                <button
                  onClick={() => {
                    setUserStatus('offline');
                    setShowProfileDropdown(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded transition-colors ${
                    userStatus === 'offline' ? 'bg-gray-700' : 'hover:bg-gray-700/50'
                  }`}
                >
                  <Circle className="w-3 h-3 fill-gray-500 text-gray-500" />
                  <span className="text-white text-sm">Offline</span>
                  {userStatus === 'offline' && <Check className="w-4 h-4 text-cyan-400 ml-auto" />}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* MIDDLE LEFT - Channels/DMs/Search List */}
      <div className="w-64 bg-gray-800/50 border-r border-gray-700 flex flex-col">
        <div className="p-3 border-b border-gray-700 flex items-center justify-between">
          <h3 className="text-white font-semibold text-sm">
            {leftMenuActive === 'channels' ? 'Channels' : 
             leftMenuActive === 'dms' ? 'Direct Messages' : 
             leftMenuActive === 'pinned' ? 'Pinned' : 
             leftMenuActive === 'search' ? 'Advanced Search' :
             leftMenuActive === 'ai' ? 'AI Assistant' :
             leftMenuActive === 'settings' ? 'Settings' :
             'Channels'}
          </h3>
          {leftMenuActive === 'channels' && (
            <button
              onClick={() => setShowCreateChannel(true)}
              className="p-1 hover:bg-gray-700 rounded transition-colors"
              title="New Channel"
            >
              <Plus className="w-4 h-4 text-gray-400" />
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto premium-scrollbar">
          {/* Channels List */}
          {leftMenuActive === 'channels' && channels.map((channel) => (
            <button
              key={channel.id}
              onClick={() => selectChannel(channel)}
              className={`w-full flex items-center gap-2 p-2 mx-1.5 my-0.5 rounded-md transition-all text-left ${
                activeChannel?.id === channel.id 
                  ? 'bg-cyan-600/20 border border-cyan-600/40' 
                  : 'hover:bg-gray-700/50'
              }`}
            >
              <Hash className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm font-medium truncate">{channel.name}</div>
              </div>
              {channel.unreadCount > 0 && (
                <span className="bg-cyan-600 text-white text-xs rounded-full px-2 py-0.5 flex-shrink-0">
                  {channel.unreadCount}
                </span>
              )}
            </button>
          ))}
          
          {/* Pinned Messages */}
          {leftMenuActive === 'pinned' && pinnedMessages.map((msg) => (
            <div
              key={msg.id}
              className="p-3 mx-1.5 my-0.5 bg-gray-700/30 rounded-md hover:bg-gray-700/50 cursor-pointer transition-colors"
              onClick={() => document.getElementById(`message-${msg.id}`)?.scrollIntoView({ behavior: 'smooth' })}
            >
              <div className="text-white text-sm font-medium mb-1">{msg.user?.name}</div>
              <div className="text-gray-300 text-xs line-clamp-2">{msg.content}</div>
            </div>
          ))}
          
          {/* Advanced Search */}
          {leftMenuActive === 'search' && (
            <div className="p-3">
              {/* Search Input */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search all messages..."
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 mb-2"
                  onChange={(e) => searchMessages(e.target.value)}
                />
                
                {/* Filters */}
                <div className="space-y-2 mt-3">
                  <div className="text-xs text-gray-400 font-semibold mb-2">Filters</div>
                  <select className="w-full bg-gray-700 text-white px-2 py-1.5 rounded text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500">
                    <option>All Channels</option>
                    {channels.map(ch => (
                      <option key={ch.id} value={ch.id}>{ch.name}</option>
                    ))}
                  </select>
                  <select className="w-full bg-gray-700 text-white px-2 py-1.5 rounded text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500">
                    <option>All Users</option>
                    {channelMembers.map(member => (
                      <option key={member.id} value={member.id}>{member.name}</option>
                    ))}
                  </select>
                  <select className="w-full bg-gray-700 text-white px-2 py-1.5 rounded text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500">
                    <option>Any Time</option>
                    <option>Today</option>
                    <option>This Week</option>
                    <option>This Month</option>
                  </select>
                </div>
              </div>
              
              {/* Search Results */}
              <div className="space-y-2">
                <div className="text-xs text-gray-400 font-semibold mb-2">
                  Results ({searchResults.length})
                </div>
                {searchResults.length === 0 ? (
                  <div className="text-center text-gray-500 text-xs py-8">
                    No results found
                  </div>
                ) : (
                  searchResults.map(result => (
                    <div
                      key={result.id}
                      className="p-2 bg-gray-700/30 rounded hover:bg-gray-700/50 cursor-pointer transition-colors"
                      onClick={() => {
                        setLeftMenuActive('channels');
                        document.getElementById(`message-${result.id}`)?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center text-white text-[10px] font-semibold">
                          {result.user?.name?.charAt(0) || '?'}
                        </div>
                        <div className="text-white text-xs font-medium">{result.user?.name}</div>
                        <div className="text-gray-500 text-[10px] ml-auto">{formatTime(result.createdAt)}</div>
                      </div>
                      <div className="text-gray-300 text-xs line-clamp-2">{result.content}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          
          {/* AI Assistant */}
          {leftMenuActive === 'ai' && (
            <div className="p-3">
              <div className="space-y-2">
                <button
                  onClick={() => handleAIFeature('summarize', () => {
                    toast.info('Generating conversation summary...');
                    // TODO: Implement AI summarization
                  })}
                  className="w-full flex items-center gap-3 p-3 bg-gray-700/30 hover:bg-gray-700/50 rounded-lg transition-colors text-left"
                >
                  <div className="w-8 h-8 bg-cyan-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="text-white text-sm font-medium">Summarize</div>
                      <span className="text-[10px] bg-green-600 px-2 py-0.5 rounded font-semibold text-white">FREE</span>
                    </div>
                    <div className="text-gray-400 text-xs">Get conversation summary</div>
                  </div>
                </button>

                <button
                  onClick={() => handleAIFeature('translate', () => {
                    toast.info('Translating messages...');
                    // TODO: Implement AI translation
                  })}
                  className="w-full flex items-center gap-3 p-3 bg-gray-700/30 hover:bg-gray-700/50 rounded-lg transition-colors text-left"
                >
                  <div className="w-8 h-8 bg-cyan-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Globe className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="text-white text-sm font-medium">Translate</div>
                      <span className="text-[10px] bg-cyan-600 px-2 py-0.5 rounded font-semibold text-white">PREMIUM</span>
                    </div>
                    <div className="text-gray-400 text-xs">Translate messages</div>
                  </div>
                </button>

                <button
                  onClick={() => handleAIFeature('sentiment', () => {
                    toast.info('Analyzing conversation sentiment...');
                    // TODO: Implement sentiment analysis
                  })}
                  className="w-full flex items-center gap-3 p-3 bg-gray-700/30 hover:bg-gray-700/50 rounded-lg transition-colors text-left"
                >
                  <div className="w-8 h-8 bg-cyan-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Heart className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="text-white text-sm font-medium">Sentiment</div>
                      <span className="text-[10px] bg-cyan-600 px-2 py-0.5 rounded font-semibold text-white">PREMIUM</span>
                    </div>
                    <div className="text-gray-400 text-xs">Analyze conversation tone</div>
                  </div>
                </button>

                <button
                  onClick={() => handleAIFeature('actionItems', () => {
                    toast.info('Extracting action items...');
                    // TODO: Implement action item extraction
                  })}
                  className="w-full flex items-center gap-3 p-3 bg-gray-700/30 hover:bg-gray-700/50 rounded-lg transition-colors text-left"
                >
                  <div className="w-8 h-8 bg-cyan-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckSquare className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="text-white text-sm font-medium">Action Items</div>
                      <span className="text-[10px] bg-cyan-600 px-2 py-0.5 rounded font-semibold text-white">PREMIUM</span>
                    </div>
                    <div className="text-gray-400 text-xs">Extract tasks & todos</div>
                  </div>
                </button>

                <button
                  onClick={() => handleAIFeature('qa', () => {
                    toast.info('AI Q&A ready! Ask your question...');
                    // TODO: Implement Q&A interface
                  })}
                  className="w-full flex items-center gap-3 p-3 bg-gray-700/30 hover:bg-gray-700/50 rounded-lg transition-colors text-left"
                >
                  <div className="w-8 h-8 bg-cyan-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <HelpCircle className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="text-white text-sm font-medium">Q&A</div>
                      <span className="text-[10px] bg-green-600 px-2 py-0.5 rounded font-semibold text-white">FREE</span>
                    </div>
                    <div className="text-gray-400 text-xs">Ask about conversation</div>
                  </div>
                </button>

                <button
                  onClick={() => handleAIFeature('suggestions', () => {
                    toast.info('Generating AI suggestions...');
                    // TODO: Implement AI suggestions
                  })}
                  className="w-full flex items-center gap-3 p-3 bg-gray-700/30 hover:bg-gray-700/50 rounded-lg transition-colors text-left"
                >
                  <div className="w-8 h-8 bg-cyan-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="text-white text-sm font-medium">Suggestions</div>
                      <span className="text-[10px] bg-purple-600 px-2 py-0.5 rounded font-semibold text-white">ENTERPRISE</span>
                    </div>
                    <div className="text-gray-400 text-xs">Get AI recommendations</div>
                  </div>
                </button>

                <button
                  onClick={() => handleAIFeature('smartReplies', () => {
                    toast.info('Generating smart replies...');
                    // TODO: Implement smart reply generation
                  })}
                  className="w-full flex items-center gap-3 p-3 bg-gray-700/30 hover:bg-gray-700/50 rounded-lg transition-colors text-left"
                >
                  <div className="w-8 h-8 bg-cyan-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="text-white text-sm font-medium">Smart Replies</div>
                      <span className="text-[10px] bg-green-600 px-2 py-0.5 rounded font-semibold text-white">FREE</span>
                    </div>
                    <div className="text-gray-400 text-xs">Generate quick responses</div>
                  </div>
                </button>
              </div>
            </div>
          )}
          
          {/* Settings */}
          {leftMenuActive === 'settings' && (
            <div className="p-3">
              <div className="space-y-2">
                {/* Notifications */}
                <div className="p-3 bg-gray-700/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Bell className="w-4 h-4 text-cyan-400" />
                      <span className="text-white text-sm font-medium">Notifications</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-9 h-5 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-cyan-600"></div>
                    </label>
                  </div>
                  <p className="text-gray-400 text-xs">Enable desktop notifications</p>
                </div>

                {/* Sound */}
                <div className="p-3 bg-gray-700/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Volume2 className="w-4 h-4 text-cyan-400" />
                      <span className="text-white text-sm font-medium">Sound</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-9 h-5 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-cyan-600"></div>
                    </label>
                  </div>
                  <p className="text-gray-400 text-xs">Play sound for new messages</p>
                </div>

                {/* Theme */}
                <div className="p-3 bg-gray-700/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Palette className="w-4 h-4 text-cyan-400" />
                    <span className="text-white text-sm font-medium">Theme</span>
                  </div>
                  <select className="w-full bg-gray-700 text-white px-3 py-2 rounded text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500">
                    <option>Dark (Default)</option>
                    <option>Light</option>
                    <option>Auto</option>
                  </select>
                </div>

                {/* Language */}
                <div className="p-3 bg-gray-700/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Globe className="w-4 h-4 text-cyan-400" />
                    <span className="text-white text-sm font-medium">Language</span>
                  </div>
                  <select className="w-full bg-gray-700 text-white px-3 py-2 rounded text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500">
                    <option>English</option>
                    <option>Spanish</option>
                    <option>French</option>
                    <option>German</option>
                    <option>Japanese</option>
                  </select>
                </div>

                {/* Privacy */}
                <div className="p-3 bg-gray-700/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-cyan-400" />
                      <span className="text-white text-sm font-medium">Read Receipts</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-9 h-5 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-cyan-600"></div>
                    </label>
                  </div>
                  <p className="text-gray-400 text-xs">Show when you've read messages</p>
                </div>

                {/* About */}
                <div className="p-3 bg-gray-700/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Info className="w-4 h-4 text-cyan-400" />
                    <span className="text-white text-sm font-medium">About</span>
                  </div>
                  <p className="text-gray-400 text-xs mb-1">Plugly AI Team Chat</p>
                  <p className="text-gray-500 text-[10px]">Version 1.0.0</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Search Bar */}
        {showSearch && (
          <div className="p-3 border-b border-gray-700 bg-gray-800/50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search messages..."
                className="w-full bg-gray-700 text-white pl-10 pr-4 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                onChange={(e) => searchMessages(e.target.value)}
              />
            </div>
            {searchResults.length > 0 && (
              <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
                {searchResults.map(result => (
                  <div
                    key={result.id}
                    className="p-2 bg-gray-700/50 rounded hover:bg-gray-700 cursor-pointer transition-colors"
                    onClick={() => {
                      setShowSearch(false);
                      setSearchResults([]);
                      // Scroll to message
                      document.getElementById(`message-${result.id}`)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-cyan-400 text-xs font-medium">{result.user?.name}</span>
                      <span className="text-gray-400 text-xs">{formatTime(result.createdAt)}</span>
                    </div>
                    <p className="text-gray-200 text-sm line-clamp-2">{result.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pinned Messages */}
        {pinnedMessages.length > 0 && (
          <div className="p-2 border-b border-gray-700 bg-cyan-900/10">
            <div className="flex items-center gap-2 overflow-x-auto">
              <Pin className="w-4 h-4 text-cyan-400 flex-shrink-0" />
              {pinnedMessages.map(msg => (
                <div
                  key={msg.id}
                  className="flex-shrink-0 px-3 py-1.5 bg-gray-800/50 rounded-lg hover:bg-gray-800 cursor-pointer transition-colors"
                  onClick={() => {
                    document.getElementById(`message-${msg.id}`)?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <p className="text-gray-200 text-xs truncate max-w-xs">{msg.content}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chat Header */}
        <div className="premium-header flex items-center justify-between py-2 px-3">
          <div className="flex items-center gap-2">
            <div>
              <h3 className="text-white text-sm font-semibold flex items-center gap-1.5">
                <Hash className="w-3.5 h-3.5 text-cyan-400" />
                {activeChannel?.name || 'Select a channel'}
              </h3>
              <p className="text-gray-400 text-[10px]">
                {channelMembers.length} members • {onlineUsers.size} online
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Search Box */}
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search messages..."
                className="w-full bg-gray-700 text-white pl-10 pr-4 py-1.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                onChange={(e) => searchMessages(e.target.value)}
              />
            </div>

            {/* Voice Call */}
            <button
              onClick={() => startCall('audio')}
              className="p-1.5 hover:bg-gray-700 rounded-md transition-colors"
              title="Start voice call"
            >
              <Phone className="w-4 h-4 text-gray-400" />
            </button>

            {/* Video Call */}
            <button
              onClick={() => startCall('video')}
              className="p-1.5 hover:bg-gray-700 rounded-md transition-colors"
              title="Start video call"
            >
              <Video className="w-4 h-4 text-gray-400" />
            </button>

            {/* Activity Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowActivityDropdown(!showActivityDropdown)}
                className="p-1.5 bg-cyan-600 hover:bg-cyan-700 rounded-md transition-colors"
                title="Activity"
                ref={(el) => {
                  if (el) {
                    window.activityButtonRef = el;
                  }
                }}
              >
                <MoreVertical className="w-4 h-4 text-white" />
              </button>
            </div>

            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-700 rounded-md transition-colors"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div 
          className="flex-1 overflow-y-auto p-4 space-y-4 premium-scrollbar relative"
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {/* Drag & Drop Overlay */}
          {isDragging && (
            <div className="absolute inset-0 bg-cyan-600/20 border-2 border-dashed border-cyan-400 rounded-lg flex items-center justify-center z-50 backdrop-blur-sm">
              <div className="text-center">
                <svg className="w-16 h-16 text-cyan-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-cyan-400 text-lg font-semibold">Drop file to upload</p>
                <p className="text-gray-400 text-sm mt-1">Images, documents, and more</p>
              </div>
            </div>
          )}
          
          {filteredMessages.filter(msg => !msg.parentId).map((message, index) => {
            const isOwnMessage = message.user?.id === currentUser.id;
            const showAvatar = index === 0 || filteredMessages.filter(m => !m.parentId)[index - 1]?.user?.id !== message.user?.id;

            return (
              <div
                id={`message-${message.id || message.tempId}`}
                key={message.id || message.tempId}
                className={`flex gap-3 group ${isOwnMessage ? 'flex-row-reverse' : ''}`}
              >
                {showAvatar && !isOwnMessage && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                    {message.user?.name?.charAt(0) || '?'}
                  </div>
                )}
                {!showAvatar && !isOwnMessage && <div className="w-8" />}

                <div className={`flex-1 max-w-[70%] ${isOwnMessage ? 'flex flex-col items-end' : ''}`}>
                  {showAvatar && (
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-white text-sm font-semibold">
                        {message.user?.name || 'Unknown'}
                      </span>
                      <span className="text-gray-400 text-xs">
                        {formatTime(message.createdAt)}
                      </span>
                    </div>
                  )}

                  <div
                    className={`premium-message ${
                      isOwnMessage
                        ? 'premium-message-own text-white'
                        : 'text-gray-100'
                    }`}
                  >
                    {message.replyTo && (
                      <div className="mb-2 pb-2 border-b border-gray-600 text-xs opacity-70">
                        <Reply className="w-3 h-3 inline mr-1" />
                        Replying to {message.replyTo.user.name}
                      </div>
                    )}

                    {message.type === 'file' && (
                      <div className="mb-2">
                        {message.metadata?.fileType?.startsWith('image/') && message.metadata?.fileUrl ? (
                          <div className="relative group">
                            <img 
                              src={message.metadata.fileUrl} 
                              alt={message.metadata.fileName}
                              className="max-w-sm max-h-64 rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => window.open(message.metadata.fileUrl, '_blank')}
                            />
                            <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => window.open(message.metadata.fileUrl, '_blank')}
                                className="p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                                title="Download"
                              >
                                <Download className="w-4 h-4 text-white" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3 p-3 bg-gray-700/30 rounded-lg hover:bg-gray-700/50 transition-colors cursor-pointer">
                            <span className="text-2xl">{getFileIcon(message.metadata?.fileName)}</span>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-white truncate">{message.metadata?.fileName}</div>
                              <div className="text-xs text-gray-400">{formatFileSize(message.metadata?.fileSize)}</div>
                            </div>
                            <button
                              onClick={() => window.open(message.metadata?.fileUrl, '_blank')}
                              className="p-2 hover:bg-gray-600 rounded transition-colors"
                              title="Download"
                            >
                              <Download className="w-4 h-4 text-gray-400" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    <p className={`text-sm whitespace-pre-wrap break-words ${message.deleted ? 'italic text-gray-500' : ''}`}>
                      {message.deleted ? (
                        <span className="flex items-center gap-1">
                          <Trash2 className="w-3 h-3" />
                          {message.content}
                        </span>
                      ) : (
                        renderMessageWithMentions(message.content)
                      )}
                    </p>

                    {/* Edited indicator */}
                    {message.edited && !message.deleted && (
                      <span className="text-xs text-gray-500 italic ml-2">(edited)</span>
                    )}

                    {/* Read receipts */}
                    <div className="flex items-center gap-1 mt-1">
                      {message.status === 'sending' && (
                        <Loader className="w-3 h-3 animate-spin" />
                      )}
                      {message.status === 'sent' && getReadStatus(message) === 'sent' && (
                        <Check className="w-3 h-3 text-gray-400" />
                      )}
                      {getReadStatus(message) === 'read-some' && (
                        <CheckCheck className="w-3 h-3 text-gray-400" />
                      )}
                      {getReadStatus(message) === 'read-all' && (
                        <CheckCheck className="w-3 h-3 text-cyan-400" />
                      )}
                      {messageReadBy[message.id]?.length > 0 && (
                        <span className="text-xs text-gray-500">
                          {messageReadBy[message.id].length} read
                        </span>
                      )}
                    </div>

                    {/* Message Reactions */}
                    {messageReactions[message.id] && Object.keys(messageReactions[message.id]).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {Object.entries(messageReactions[message.id]).map(([emoji, count]) => (
                          <button
                            key={emoji}
                            onClick={() => addReaction(message.id, emoji)}
                            className="flex items-center gap-1 px-2 py-0.5 bg-gray-700/50 hover:bg-gray-700 rounded-full text-xs transition-colors"
                          >
                            <span>{emoji}</span>
                            <span className="text-gray-400">{count}</span>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Message Actions */}
                    <div className="flex items-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      {/* Quick Reactions */}
                      <div className="flex gap-1">
                        {['👍', '❤️', '😂', '🎉', '👀'].map(emoji => (
                          <button
                            key={emoji}
                            onClick={() => addReaction(message.id, emoji)}
                            className="text-sm hover:scale-125 transition-transform"
                            title={`React with ${emoji}`}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                      
                      {/* Reply Button */}
                      <button
                        onClick={() => replyToMessage(message)}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-cyan-400 hover:bg-gray-700/50 rounded transition-colors"
                        title="Reply to message"
                      >
                        <Reply className="w-3 h-3" />
                        <span>Reply</span>
                      </button>

                      {/* View Replies Button (if has replies) */}
                      {(threadReplies[message.id]?.length > 0 || messages.filter(m => m.parentId === message.id).length > 0) && (
                        <button
                          onClick={() => toggleThread(message.id)}
                          className="flex items-center gap-1 px-2 py-1 text-xs text-cyan-400 hover:bg-gray-700/50 rounded transition-colors"
                          title={expandedThreads.has(message.id) ? "Hide replies" : "Show replies"}
                        >
                          <ChevronDown className={`w-3 h-3 transition-transform ${expandedThreads.has(message.id) ? 'rotate-180' : ''}`} />
                          <span>{messages.filter(m => m.parentId === message.id).length} {messages.filter(m => m.parentId === message.id).length === 1 ? 'reply' : 'replies'}</span>
                        </button>
                      )}

                      {/* Edit Button (only for own messages) */}
                      {message.user?.id === currentUser.id && !message.deleted && (
                        <button
                          onClick={() => startEditMessage(message)}
                          className="p-1 text-gray-400 hover:text-cyan-400 hover:bg-gray-700/50 rounded transition-colors"
                          title="Edit message"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      )}

                      {/* Delete Button (only for own messages) */}
                      {message.user?.id === currentUser.id && !message.deleted && (
                        <button
                          onClick={() => deleteMessage(message.id)}
                          className="p-1 text-gray-400 hover:text-red-400 hover:bg-gray-700/50 rounded transition-colors"
                          title="Delete message"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}

                      {/* Pin Button */}
                      <button
                        onClick={() => pinMessage(message)}
                        className={`p-1 rounded transition-colors ${
                          pinnedMessages.find(m => m.id === message.id)
                            ? 'text-cyan-400 bg-cyan-400/10'
                            : 'text-gray-400 hover:text-cyan-400 hover:bg-gray-700/50'
                        }`}
                        title={pinnedMessages.find(m => m.id === message.id) ? 'Unpin' : 'Pin message'}
                      >
                        <Pin className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Inline Thread Replies */}
                {expandedThreads.has(message.id) && (
                  <div className="ml-11 mt-2 space-y-2 border-l-2 border-cyan-600/30 pl-4">
                    {messages
                      .filter(m => m.parentId === message.id)
                      .map(reply => (
                        <div key={reply.id || reply.tempId} className="flex gap-2">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                            {reply.user?.name?.charAt(0) || '?'}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-white text-xs font-medium">{reply.user?.name}</span>
                              <span className="text-gray-400 text-[10px]">{formatTime(reply.createdAt)}</span>
                            </div>
                            <div className="bg-gray-800/50 rounded-lg px-3 py-2">
                              <p className="text-gray-100 text-sm">{reply.content}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Typing Indicator */}
          {typingUsers.length > 0 && (
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <span>{typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...</span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Edit Preview */}
        {editingMessage && (
          <div className="px-4 py-2 bg-cyan-900/20 border-t border-cyan-700 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span className="text-cyan-400">Editing message</span>
            </div>
            <button onClick={cancelEdit}>
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        )}

        {/* Reply Preview */}
        {replyTo && !editingMessage && (
          <div className="px-4 py-2 bg-gray-800 border-t border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <Reply className="w-4 h-4 text-cyan-400" />
              <span className="text-gray-400">Replying to</span>
              <span className="text-white">{replyTo.user.name}</span>
            </div>
            <button onClick={() => setReplyTo(null)}>
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        )}

        {/* File Preview */}
        {selectedFile && (
          <div className="px-4 py-3 bg-gray-800 border-t border-gray-700">
            <div className="flex items-start gap-3">
              {filePreview ? (
                <img 
                  src={filePreview} 
                  alt="Preview" 
                  className="w-16 h-16 object-cover rounded"
                />
              ) : (
                <div className="w-16 h-16 bg-gray-700 rounded flex items-center justify-center text-2xl">
                  {getFileIcon(selectedFile.name)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">{selectedFile.name}</div>
                <div className="text-xs text-gray-400">{formatFileSize(selectedFile.size)}</div>
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="mt-2">
                    <div className="w-full bg-gray-700 rounded-full h-1.5">
                      <div 
                        className="bg-cyan-600 h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-400 mt-1">{uploadProgress}%</div>
                  </div>
                )}
              </div>
              <button 
                onClick={() => {
                  setSelectedFile(null);
                  setFilePreview(null);
                  setUploadProgress(0);
                }}
                className="p-1 hover:bg-gray-700 rounded transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </div>
        )}

        {/* Message Input */}
        <div className="p-4 relative">
          {/* Mentions Dropdown */}
          {showMentions && filteredMembersForMention.length > 0 && (
            <div className="absolute bottom-full left-4 right-4 mb-2 bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-h-48 overflow-y-auto z-50">
              {filteredMembersForMention.map((member) => (
                <button
                  key={member.id}
                  onClick={() => insertMention(member)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-gray-700 transition-colors text-left"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center text-white text-sm font-semibold">
                    {member.name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <div className="text-white text-sm font-medium">{member.name}</div>
                    <div className="text-gray-400 text-xs">{member.email || 'Team member'}</div>
                  </div>
                  {onlineUsers.has(member.id) && (
                    <div className="ml-auto w-2 h-2 bg-green-500 rounded-full" />
                  )}
                </button>
              ))}
            </div>
          )}

          <div className="premium-input-container">
            <div className="premium-input-inner">
              <textarea
                value={messageInput}
                onChange={handleTyping}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !showMentions) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder={`Message #${activeChannel?.name || 'channel'}`}
                className="premium-textarea"
                rows="1"
                style={{ maxHeight: '120px' }}
              />
              
              <div className="premium-options">
                <div className="premium-btns-add">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="premium-btn-icon"
                    title="Attach file"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                  <div className="relative">
                    <button 
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      className="premium-btn-icon"
                      title="Add emoji"
                      ref={(el) => {
                        if (el) {
                          window.emojiButtonRef = el;
                        }
                      }}
                    >
                      <Smile className="w-4 h-4" />
                    </button>
                  </div>
                  <button 
                    onClick={() => {
                      setMessageInput(prev => prev + '@');
                      setShowMentions(true);
                      setMentionPosition(messageInput.length);
                    }}
                    className="premium-btn-icon" 
                    title="Mention user"
                  >
                    <AtSign className="w-4 h-4" />
                  </button>
                </div>

                <button
                  onClick={sendMessage}
                  disabled={!messageInput.trim() && !selectedFile}
                  className="premium-btn-submit"
                  title="Send message"
                >
                  <div className="premium-btn-submit-inner">
                    <Send className="w-4 h-4" />
                  </div>
                </button>
              </div>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      </div>


      {/* Create Channel Modal */}
      {showCreateChannel && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-96 border border-gray-700">
            <h3 className="text-white text-lg font-semibold mb-4">Create Channel</h3>
            <input
              type="text"
              value={newChannelName}
              onChange={(e) => setNewChannelName(e.target.value)}
              placeholder="Channel name"
              className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
            <textarea
              value={newChannelDesc}
              onChange={(e) => setNewChannelDesc(e.target.value)}
              placeholder="Description (optional)"
              className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
              rows="3"
            />
            <div className="flex gap-2">
              <button
                onClick={handleCreateChannel}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg py-2 transition-colors"
              >
                Create
              </button>
              <button
                onClick={() => setShowCreateChannel(false)}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white rounded-lg py-2 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-[9999]">
          <div className="bg-gray-800 rounded-lg p-6 w-96 border border-gray-700">
            <h3 className="text-white text-lg font-semibold mb-4 flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Delete Message
            </h3>
            <p className="text-gray-300 mb-6">
              Are you sure you want to delete this message? This action cannot be undone.
            </p>
            <div className="flex gap-2">
              <button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-lg py-2 transition-colors"
              >
                Delete
              </button>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white rounded-lg py-2 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Call Modal */}
      {showVideoCall && (
        <div className="fixed inset-0 bg-black/90 z-[10000] flex items-center justify-center">
          <div className="w-full h-full max-w-7xl max-h-screen p-4 flex flex-col">
            {/* Call Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                  {callType === 'video' ? <Video className="w-5 h-5 text-white" /> : <Phone className="w-5 h-5 text-white" />}
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg">
                    {callType === 'video' ? 'Video Call' : 'Audio Call'} - {activeChannel?.name}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {callParticipants.length} {callParticipants.length === 1 ? 'participant' : 'participants'}
                  </p>
                </div>
              </div>
              <button
                onClick={endCall}
                className="p-2 bg-red-600 hover:bg-red-700 rounded-full transition-colors"
                title="End call"
              >
                <X className="w-6 h-6 text-white" />
              </button>
            </div>

            {/* Video Grid */}
            <div className="flex-1 grid grid-cols-2 gap-4 mb-4">
              {/* Local Video */}
              <div className="relative bg-gray-900 rounded-lg overflow-hidden">
                <video
                  ref={localVideoRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-4 left-4 bg-black/50 px-3 py-1 rounded-full">
                  <span className="text-white text-sm font-medium">You {isCameraOff && '(Camera Off)'}</span>
                </div>
                {isCameraOff && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center text-white text-2xl font-bold">
                      {currentUser.name?.charAt(0) || '?'}
                    </div>
                  </div>
                )}
              </div>

              {/* Remote Video */}
              <div className="relative bg-gray-900 rounded-lg overflow-hidden">
                <video
                  ref={remoteVideoRef}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                  <div className="text-center">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                      ?
                    </div>
                    <p className="text-gray-400">Waiting for others to join...</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Call Controls */}
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={toggleMute}
                className={`p-4 rounded-full transition-colors ${
                  isMuted ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-700 hover:bg-gray-600'
                }`}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? (
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" clipRule="evenodd" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                )}
              </button>

              {callType === 'video' && (
                <button
                  onClick={toggleCamera}
                  className={`p-4 rounded-full transition-colors ${
                    isCameraOff ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  title={isCameraOff ? 'Turn on camera' : 'Turn off camera'}
                >
                  {isCameraOff ? (
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
                    </svg>
                  ) : (
                    <Video className="w-6 h-6 text-white" />
                  )}
                </button>
              )}

              {callType === 'video' && (
                <button
                  onClick={isScreenSharing ? stopScreenShare : startScreenShare}
                  className={`p-4 rounded-full transition-colors ${
                    isScreenSharing ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </button>
              )}

              <button
                onClick={endCall}
                className="p-4 bg-red-600 hover:bg-red-700 rounded-full transition-colors"
                title="End call"
              >
                <Phone className="w-6 h-6 text-white transform rotate-135" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Assist Panel */}
      {showAIAssist && activeChannel && (
        <AIAssistPanel
          channelId={activeChannel.id}
          onClose={() => setShowAIAssist(false)}
        />
      )}

      {/* Emoji Picker Portal */}
      {showEmojiPicker && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 z-[10000]" 
          onClick={() => setShowEmojiPicker(false)}
        >
          <div 
            className="fixed rounded-xl shadow-2xl overflow-hidden"
            style={{
              bottom: window.emojiButtonRef ? `${window.innerHeight - window.emojiButtonRef.getBoundingClientRect().top + 10}px` : '100px',
              left: window.emojiButtonRef ? `${window.emojiButtonRef.getBoundingClientRect().left}px` : '50%',
              width: 'fit-content',
              maxWidth: '500px',
              height: '435px',
              backgroundColor: 'transparent'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <EmojiPicker onEmojiSelect={handleEmojiClick}>
              <EmojiPickerSearch placeholder="Search emoji..." />
              <EmojiPickerContent />
              <EmojiPickerFooter />
            </EmojiPicker>
          </div>
        </div>,
        document.body
      )}

      {/* Activity Dropdown Portal */}
      {showActivityDropdown && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 z-[10000]" 
          onClick={() => setShowActivityDropdown(false)}
        >
          <div 
            className="fixed bg-gray-800 border border-gray-700 rounded-lg shadow-xl"
            style={{
              top: window.activityButtonRef ? `${window.activityButtonRef.getBoundingClientRect().bottom + 5}px` : '100px',
              right: window.activityButtonRef ? `${window.innerWidth - window.activityButtonRef.getBoundingClientRect().right}px` : '20px',
              width: '192px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                setRightPanelTab('members');
                setShowRightPanel(true);
                setShowActivityDropdown(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-700 transition-colors text-left rounded-t-lg"
            >
              <Users className="w-4 h-4 text-gray-400" />
              <span className="text-white text-sm">Members</span>
            </button>
            <button
              onClick={() => {
                setRightPanelTab('pinned');
                setShowRightPanel(true);
                setShowActivityDropdown(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-700 transition-colors text-left"
            >
              <Pin className="w-4 h-4 text-gray-400" />
              <span className="text-white text-sm">Pinned</span>
            </button>
            <button
              onClick={() => {
                setRightPanelTab('files');
                setShowRightPanel(true);
                setShowActivityDropdown(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-700 transition-colors text-left"
            >
              <Folder className="w-4 h-4 text-gray-400" />
              <span className="text-white text-sm">Files</span>
            </button>
            <button
              onClick={() => {
                setRightPanelTab('activity');
                setShowRightPanel(true);
                setShowActivityDropdown(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-700 transition-colors text-left rounded-b-lg"
            >
              <Bell className="w-4 h-4 text-gray-400" />
              <span className="text-white text-sm">Activity</span>
            </button>
          </div>
        </div>,
        document.body
      )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
};

export default TeamChatModal;
